import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { astrologyService } from '../services/astrologyService';
import './IndianAstrology.css';

const IndianAstrology: React.FC = () => {
  const [selectedNakshatra, setSelectedNakshatra] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [kundliResult, setKundliResult] = useState<any>(null);
  const [cities, setCities] = useState<string[]>([]);

  const nakshatras = [
    'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra',
    'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
    'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
    'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha',
    'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'
  ];

  const rashis = [
    { name: 'Aries', symbol: '♈', sanskrit: 'Mesha' },
    { name: 'Taurus', symbol: '♉', sanskrit: 'Vrishabha' },
    { name: 'Gemini', symbol: '♊', sanskrit: 'Mithuna' },
    { name: 'Cancer', symbol: '♋', sanskrit: 'Karka' },
    { name: 'Leo', symbol: '♌', sanskrit: 'Simha' },
    { name: 'Virgo', symbol: '♍', sanskrit: 'Kanya' },
    { name: 'Libra', symbol: '♎', sanskrit: 'Tula' },
    { name: 'Scorpio', symbol: '♏', sanskrit: 'Vrishchik' },
    { name: 'Sagittarius', symbol: '♐', sanskrit: 'Dhanu' },
    { name: 'Capricorn', symbol: '♑', sanskrit: 'Makara' },
    { name: 'Aquarius', symbol: '♒', sanskrit: 'Kumbha' },
    { name: 'Pisces', symbol: '♓', sanskrit: 'Meena' }
  ];

  useEffect(() => {
    // Load cities from API
    const loadCities = async () => {
      try {
        const data = await astrologyService.getCities();
        if (data.status === 'success') {
          setCities(data.cities);
        }
      } catch (err) {
        console.error('Failed to load cities:', err);
        // Fallback to a basic list of major cities
        setCities([
          "Delhi", "Mumbai", "Bangalore", "Hyderabad", "Chennai", "Kolkata", "Pune", "Ahmedabad",
          "Jaipur", "Lucknow", "Kochi", "Visakhapatnam", "Indore", "Chandigarh", "Nagpur", "Vadodara",
          "Aurangabad", "Coimbatore", "Trivandrum", "Ludhiana", "Varanasi", "Kanpur", "Prayagraj", "Patna",
          "Ranchi", "Bhopal", "Raipur", "Guwahati", "Ghaziabad", "Noida", "Gurugram", "Faridabad"
        ]);
      }
    };

    loadCities();
  }, []);

  const handleGenerateKundli = async () => {
    if (!birthDate || !birthTime || !birthPlace) {
      setError('Please fill in all birth details');
      return;
    }

    setLoading(true);
    setError('');
    setKundliResult(null);

    try {
      const result = await astrologyService.generateKundli({
        birth_date: birthDate,
        birth_time: birthTime,
        birth_city: birthPlace,
      });

      if (result.status === 'success') {
        setKundliResult(result);
      } else {
        setError(result.error || 'Failed to generate Kundli');
      }
    } catch (err: any) {
      console.error('Kundli Error:', err);
      setError(err.response?.data?.detail || 'Error connecting to astrology server. Please ensure the API server is running.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="indian-astrology-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">Discover Your Cosmic Destiny</h1>
          <p className="hero-subtitle">Ancient Vedic wisdom meets modern insight</p>
          <div className="hero-ornament">
            <span className="ornament-symbol">☉</span>
            <span className="ornament-line"></span>
            <span className="ornament-symbol">☽</span>
          </div>
        </div>
      </section>

      {/* Kundli Generator */}
      <section className="kundli-section">
        <div className="section-container">
          <h2 className="section-title">Generate Your Kundli</h2>
          <p className="section-subtitle">Get your personalized birth chart analysis</p>
          
          <div className="vedic-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="birthDate">Birth Date</label>
                <input
                  type="date"
                  id="birthDate"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="birthTime">Birth Time</label>
                <input
                  type="time"
                  id="birthTime"
                  value={birthTime}
                  onChange={(e) => setBirthTime(e.target.value)}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="birthPlace">Birth Place</label>
                <input
                  type="text"
                  id="birthPlace"
                  value={birthPlace}
                  onChange={(e) => setBirthPlace(e.target.value)}
                  placeholder="Enter your birth place"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="nakshatra">Nakshatra (Optional)</label>
                <select
                  id="nakshatra"
                  value={selectedNakshatra}
                  onChange={(e) => setSelectedNakshatra(e.target.value)}
                  className="form-select"
                >
                  <option value="">Select Nakshatra</option>
                  {nakshatras.map((nakshatra) => (
                    <option key={nakshatra} value={nakshatra}>
                      {nakshatra}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <button 
              onClick={handleGenerateKundli} 
              className="calculate-btn"
              disabled={loading}
            >
              {loading ? 'Generating...' : 'Generate Kundli'}
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          {kundliResult && (
            <div className="indian-result">
              <h3>Your Kundli Analysis</h3>
              <div className="result-stats">
                <div className="stat-box">
                  <span className="stat-label">Sun Sign</span>
                  <strong className="stat-value">{kundliResult.sun_sign}</strong>
                </div>
                <div className="stat-box">
                  <span className="stat-label">Moon Sign</span>
                  <strong className="stat-value">{kundliResult.moon_sign}</strong>
                </div>
              </div>
              <div className="horoscope-text markdown-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {kundliResult.horoscope}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Zodiac Signs */}
      <section className="zodiac-section">
        <div className="section-container">
          <h2 className="section-title">12 Rashis (Zodiac Signs)</h2>
          <p className="section-subtitle">Explore your celestial identity</p>
          
          <div className="zodiac-grid">
            {rashis.map((rashi) => (
              <div key={rashi.name} className="zodiac-card">
                <div className="zodiac-symbol">{rashi.symbol}</div>
                <h3 className="zodiac-name">{rashi.name}</h3>
                <p className="zodiac-sanskrit">{rashi.sanskrit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Nakshatras */}
      <section className="nakshatra-section">
        <div className="section-container">
          <h2 className="section-title">27 Nakshatras</h2>
          <p className="section-subtitle">Lunar mansions that guide your destiny</p>
          
          <div className="nakshatra-grid">
            {nakshatras.map((nakshatra, index) => (
              <div key={nakshatra} className="nakshatra-card">
                <div className="nakshatra-number">{index + 1}</div>
                <h3 className="nakshatra-name">{nakshatra}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="services-section">
        <div className="section-container">
          <h2 className="section-title">Our Astrological Services</h2>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">📿</div>
              <h3>Kundli Analysis</h3>
              <p>Comprehensive birth chart reading with detailed predictions</p>
            </div>
            <div className="service-card">
              <div className="service-icon">💑</div>
              <h3>Match Making</h3>
              <p>Compatibility analysis for marriage and relationships</p>
            </div>
            <div className="service-card">
              <div className="service-icon">🔮</div>
              <h3>Muhurat</h3>
              <p>Auspicious timing for important life events</p>
            </div>
            <div className="service-card">
              <div className="service-icon">💎</div>
              <h3>Gemstone Recommendation</h3>
              <p>Personalized gemstone suggestions based on your chart</p>
            </div>
            <div className="service-card">
              <div className="service-icon">🙏</div>
              <h3>Remedies</h3>
              <p>Vedic remedies to overcome planetary obstacles</p>
            </div>
            <div className="service-card">
              <div className="service-icon">📈</div>
              <h3>Yearly Predictions</h3>
              <p>Detailed forecasts for the coming year</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndianAstrology;
